import React from 'react';
import StreamingResult from '../result/StreamingResult';
import { Body5, Container } from '@ui';
import styled from 'styled-components';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import { AgentSelectInput } from './form/AgentSelectInput';
import { Field } from './form/Field';
import { PrefilledQuestionInput } from './form/PrefilledQuestionInput';
import { FormStateTagInput } from './form/TagInput/FormStateTagInput';
import { HeaderLine } from '@ui/HeaderLine';
import { QueryControllerContext } from '@/contexts/QueryControllerContext';
import { Sources } from './form/Sources';
import { InfoIconTypes } from '@constants/infoIcons';
import { handle400Statuses, sendApiRequest } from '@services/api';
import { logNetRequest } from '@utils/usefulFuncs';
import { FormStateContext, FormStateProvider } from '@/contexts/FormStateContext';
import { Working } from '@ui/Working';
import { RUNNING_STATUS } from '@/contexts/ResultContext';
import { ModeContext } from '@/contexts/ModeContext';
import { INPUT_KEYS } from '@constants';
import { FormState } from '@ui/FormState';

const Wrapper = styled.div``;
const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
const FormActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  text-align: center;
  padding-top: 24px;
`;

const defaultFormState = {
  agentName: '',
  selectedDocs: [],
  tags: [],
  question: '',
};

const SubmitButton = ({ onClick }) => {
  const { state: formState } = React.useContext(FormStateContext);

  const handleSubmit = () => {
    const data = {
      ...formState,
    };
    onClick(data);
  };

  return (
    <FlatButton fullWidth onClick={handleSubmit}>
      Submit
    </FlatButton>
  );
};

const ResetButton = ({ onClick }) => {
  const { setFormStateValue } = React.useContext(FormStateContext);

  const handleResetClick = () => {
    setFormStateValue(null);
    onClick();
  };

  return (
    <TransparentFlatButton fullWidth onClick={handleResetClick}>
      Reset
    </TransparentFlatButton>
  );
};

const DefaultDocsSync = () => {
  const { autoSelectDefaultDocuments, tool } = React.useContext(ModeContext);
  const { setFormStateValue } = React.useContext(FormStateContext);

  React.useEffect(() => {
    const canceled = { isCanceled: false };
    const fetchDefaultDocs = async () => {
      const URL = 'v1/genai/documents/default?user_mode=' + tool;
      const { status, data } = await sendApiRequest('get', URL);
      logNetRequest(URL, status, data);
      handle400Statuses(status);
      if (status === 200 && !canceled.isCanceled) {
        if (data.documents.length > 0) {
          setFormStateValue('selectedDocs', data.documents);
        }
      }
    };

    if (autoSelectDefaultDocuments) {
      fetchDefaultDocs();
    }
    return () => {
      canceled.isCanceled = true;
    };
  }, [autoSelectDefaultDocuments, setFormStateValue, tool]);

  return null;
};

const showInput = (inputs, key) => inputs == null || Boolean(inputs[key]);

export const GenericQuery = ({ heading, resultAnchorId }) => {
  const {
    resultId,
    prevQueryFormData,
    runningStatus,
    error,
    submitQuery,
    reset: queryControllerReset,
  } = React.useContext(QueryControllerContext);
  const { inputs, isReasoningQuery } = React.useContext(ModeContext);

  const handleSubmit = (data) => {
    submitQuery(data);
  };

  const handleReset = () => {
    queryControllerReset();
  };

  React.useEffect(() => {
    if (!resultAnchorId || !resultId) return undefined;

    const element = document.getElementById(resultAnchorId);
    const yOffset = -10;
    if (!element) return undefined;

    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
    const timeout = setTimeout(() => {
      window.scrollTo({ top: y, behavior: 'smooth' });
    }, 600);

    return () => clearTimeout(timeout);
  }, [resultAnchorId, resultId]);

  const isAgentScript = prevQueryFormData?.agentName !== '';
  return (
    <Wrapper>
      {heading || null}
      <FormStateProvider initialStateParam={defaultFormState}>
        <FormWrapper>
          <DefaultDocsSync />
          {showInput(inputs, INPUT_KEYS.companiesTags) && (
            <Field label="Companies / Tags" infoIcon={InfoIconTypes.TICKER_SELECT} component={<FormStateTagInput />} />
          )}
          {showInput(inputs, INPUT_KEYS.agent) && <Field label="Agent" component={<AgentSelectInput />} />}
          {showInput(inputs, INPUT_KEYS.question) && <PrefilledQuestionInput />}
          {showInput(inputs, INPUT_KEYS.sources) && (
            <Field label="Sources" infoIcon={InfoIconTypes.SOURCES} component={<Sources />} />
          )}
          <FormActionsWrapper>
            <Container centerAll>
              <FormState error={error} />
            </Container>
            <SubmitButton onClick={handleSubmit} />
            <ResetButton onClick={handleReset} />
          </FormActionsWrapper>
        </FormWrapper>
      </FormStateProvider>

      {resultAnchorId ? <div id={resultAnchorId} /> : null}
      {resultId && (
        <Container top={24}>
          <HeaderLine isLowContrast />

          <Container top={36}>
            {prevQueryFormData?.question && (
              <Container>
                <Body5 bold>Question: </Body5>
                <Container top={16} left={16}>
                  <Body5>{prevQueryFormData.displayQuestion || prevQueryFormData.question}</Body5>
                </Container>
              </Container>
            )}
            {runningStatus === RUNNING_STATUS.RUNNING ? (
              <Container top={24} bottom={400}>
                <Working />
              </Container>
            ) : (
              <Container top={24}>
                <StreamingResult
                  resultId={resultId}
                  isAgentScriptResult={isAgentScript}
                  isReasoningQueryResult={isReasoningQuery}
                />
              </Container>
            )}
          </Container>
        </Container>
      )}
    </Wrapper>
  );
};
